import React, { useEffect, useState } from 'react';
import {
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  Autocomplete,
  Box,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { PageTitle, ListLayoutPanel } from '../../../../components/common';
import { ConfiguracionCuentaService } from '../services/configuracionService';
import { EjercicioService } from '../../ejercicios/services/ejercicioService';
import { PlanCuentaService } from '../../plan-cuentas/services/planCuentaService';
import type { ConfiguracionCuenta } from '../types/configuracion';
import type { EjercicioContable } from '../../ejercicios/types/ejercicio';
import type { PlanCuenta } from '../../plan-cuentas/types/planCuenta';

const ConfiguracionContablePage: React.FC = () => {
  const [ejercicios, setEjercicios] = useState<EjercicioContable[]>([]);
  const [selectedEjercicio, setSelectedEjercicio] = useState<number>(0);
  const [config, setConfig] = useState<Partial<ConfiguracionCuenta>>({});
  const [cuentas, setCuentas] = useState<PlanCuenta[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const [ejData, ctasData] = await Promise.all([
        EjercicioService.getAll(),
        PlanCuentaService.getCuentasMovimiento(),
      ]);
      setEjercicios(ejData);
      setCuentas(ctasData);
      if (ejData.length > 0) {
        setSelectedEjercicio(ejData[0].idEjercicio);
      }
    } catch (err) {
      setError('Error al cargar datos iniciales');
    }
  };

  useEffect(() => {
    if (selectedEjercicio) {
      loadConfiguracion(selectedEjercicio);
    }
  }, [selectedEjercicio]);

  const loadConfiguracion = async (idEjercicio: number) => {
    setLoading(true);
    try {
      const data = await ConfiguracionCuentaService.getByEjercicio(idEjercicio);
      setConfig(data || { idConfiguracion: 0, idEjercicio });
    } catch (err) {
      // May be 404 if no config exists yet
      setConfig({ idConfiguracion: 0, idEjercicio });
    } finally {
      setLoading(false);
    }
  };

  const handleCuentaChange = (field: string, cuenta: PlanCuenta | null) => {
    setConfig(prev => ({ ...prev, [field]: cuenta?.codigoCuenta || null }));
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await ConfiguracionCuentaService.save({ ...config, idEjercicio: selectedEjercicio });
      setSuccess(true);
    } catch (err) {
      setError('Error al guardar la configuración');
    } finally {
      setLoading(false);
    }
  };

  const renderCuentaField = (label: string, field: keyof ConfiguracionCuenta) => (
    <Grid size={{ xs: 12, sm: 6 }}>
      <Autocomplete
        size="small"
        options={cuentas}
        getOptionLabel={(opt) => `${opt.codigoCuenta} - ${opt.descripcion}`}
        value={cuentas.find(c => c.codigoCuenta === config[field]) || null}
        onChange={(_e, newVal) => handleCuentaChange(field, newVal)}
        renderInput={(params) => <TextField {...params} label={label} />}
      />
    </Grid>
  );

  return (
    <ListLayoutPanel>
      <PageTitle
        title="Configuración de Cuentas"
        subtitle="Definición de cuentas maestras por ejercicio"
        actions={
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={loading}
          >
            Guardar
          </Button>
        }
      />

      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess(false)} sx={{ mb: 2 }}>Configuración guardada correctamente</Alert>}

      <Box sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Ejercicio</InputLabel>
          <Select
            value={selectedEjercicio}
            label="Ejercicio"
            onChange={(e) => setSelectedEjercicio(Number(e.target.value))}
          >
            {ejercicios.map(ej => (
              <MenuItem key={ej.idEjercicio} value={ej.idEjercicio}>{ej.anio}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {loading ? <CircularProgress /> : (
        <Paper sx={{ p: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Impuestos</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {renderCuentaField('Cuenta IGV', 'cuentaIGV')}
            {renderCuentaField('Cuenta ISC', 'cuentaISC')}
            {renderCuentaField('Cuenta Percepción', 'cuentaPercepcion')}
            {renderCuentaField('Cuenta Retención', 'cuentaRetencion')}
            {renderCuentaField('Cuenta Detracción', 'cuentaDetraccion')}
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Documentos por Cobrar/Pagar</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {renderCuentaField('Doc. por Cobrar MN', 'cuentaDocPorCobrarMN')}
            {renderCuentaField('Doc. por Cobrar ME', 'cuentaDocPorCobrarME')}
            {renderCuentaField('Doc. por Pagar MN', 'cuentaDocPorPagarMN')}
            {renderCuentaField('Doc. por Pagar ME', 'cuentaDocPorPagarME')}
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Diferencia de Cambio</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {renderCuentaField('DC Ganancia', 'cuentaDCGanancia')}
            {renderCuentaField('DC Pérdida', 'cuentaDCPerdida')}
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Cierre y Resultado</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {renderCuentaField('Resultado del Ejercicio', 'cuentaResultadoEjercicio')}
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Honorarios</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {renderCuentaField('Honorarios Soles', 'cuentaHonorariosSoles')}
            {renderCuentaField('Honorarios Dólares', 'cuentaHonorariosDolares')}
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Cuentas Asociadas</Typography>
          <Grid container spacing={2}>
            {renderCuentaField('Cuenta Asociada Cliente', 'cuentaAsociadaCliente')}
            {renderCuentaField('Cuenta Asociada Proveedor', 'cuentaAsociadaProveedor')}
          </Grid>
        </Paper>
      )}
    </ListLayoutPanel>
  );
};

export default ConfiguracionContablePage;
